import SuiAlert from "components/SuiAlert";
import moment from "moment";
import { useState } from "react";
import { toast } from "react-toastify";
import { components } from "react-select";
import { paymentOptions } from "constant";
import { getFormattedClientName } from "./helper";

export const validateEmail = (email) => {
  return String(email)
    .toLowerCase()
    .match(
      /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
    );
};

export const validatePhone = (num) => {
  return true;
};

export const validateGST = (gst) => {
  return String(gst).match(
    /\d{2}[A-Z]{5}\d{4}[A-Z]{1}[A-Z\d]{1}[Z]{1}[A-Z\d]{1}/
  );
};

export const parseStrictNumber = (value, fallback = 0) => {
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : fallback;
  }

  const normalizedValue = String(value ?? "").trim();
  if (!/^\d+(\.\d+)?$/.test(normalizedValue)) {
    return fallback;
  }

  const parsedValue = Number(normalizedValue);
  return Number.isFinite(parsedValue) ? parsedValue : fallback;
};

// Catalog prices can be stored as messy strings; this keeps them numeric instead of 0.
export const parsePriceValue = (value, fallback = 0) => {
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : fallback;
  }

  if (value == null || value === "") {
    return fallback;
  }

  const normalizedValue = String(value).replace(/,/g, "").trim();
  const parsedValue = parseFloat(normalizedValue);
  return Number.isFinite(parsedValue) ? parsedValue : fallback;
};

export const normalizeCurrencyInput = (value) => {
  const normalizedValue = String(value ?? "");

  if (normalizedValue === "") {
    return "";
  }

  if (!/^\d+(\.\d{0,2})?$/.test(normalizedValue)) {
    return null;
  }

  return normalizedValue.replace(/^0+(?=\d)/, "");
};

export const roundCurrency = (value) =>
  Math.round((parseStrictNumber(value) + Number.EPSILON) * 100) / 100;

export function validatePassword(password) {
  var MinLength = 6;
  var MaxLength = 15;

  let meetsLengthRequirements =
    password.length >= MinLength && password.length <= MaxLength;
  if (!meetsLengthRequirements) {
    return {
      isValid: false,
      message: "Password needs to be in between 6-15 characters!!",
    };
  }
  var hasUpperCasevarter = false;
  var hasLowerCasevarter = false;
  var hasDecimalDigit = false;
  var hasSpecialChar = false;

  if (meetsLengthRequirements) {
    for (var i = 0, len = password.length; i < len; i++) {
      var char = password.charAt(i);
      if (!isNaN(+char * 1)) {
        hasDecimalDigit = true;
      }
      if (
        char === "@" ||
        char === "$" ||
        char === "#" ||
        char === "&" ||
        char === "%" ||
        char === "!"
      ) {
        hasSpecialChar = true;
      } else {
        if (char === char.toUpperCase()) {
          hasUpperCasevarter = true;
        }
        if (char === char.toLowerCase()) {
          hasLowerCasevarter = true;
        }
      }
    }
  }

  if (!hasUpperCasevarter) {
    return {
      isValid: false,
      message: "Password does not contain Uppercase Character!!",
    };
  }

  if (!hasLowerCasevarter) {
    return {
      isValid: false,
      message: "Password does not contain Lowercase Character!!",
    };
  }

  if (!hasDecimalDigit) {
    return {
      isValid: false,
      message: "Password does not contain a numeric character!!",
    };
  }
  if (!hasSpecialChar) {
    return {
      isValid: false,
      message: "Password does not contain Special Character!!",
    };
  }

  return { isValid: true, message: null };
}

export const getDiscountedPrice = (price, discount) => {
  return parsePriceValue(price) - parseStrictNumber(discount);
};

export const InputOption = ({
  getStyles,
  Icon,
  isDisabled,
  isFocused,
  isSelected,
  children,
  innerProps,
  ...rest
}) => {
  const [isActive, setIsActive] = useState(false);
  const onMouseDown = () => setIsActive(true);
  const onMouseUp = () => setIsActive(false);
  const onMouseLeave = () => setIsActive(false);

  // styles
  let bg = "transparent";
  if (isFocused) bg = "#eee";
  if (isActive) bg = "#B2D4FF";

  const style = {
    alignItems: "center",
    backgroundColor: bg,
    color: "inherit",
    display: "flex ",
  };

  // prop assignment
  const props = {
    ...innerProps,
    onMouseDown,
    onMouseUp,
    onMouseLeave,
    style,
  };

  return (
    <components.Option
      {...rest}
      isDisabled={isDisabled}
      isFocused={isFocused}
      isSelected={isSelected}
      getStyles={getStyles}
      innerProps={props}
    >
      <input type="checkbox" checked={isSelected} style={{ marginRight: 10 }} />
      {children}
    </components.Option>
  );
};

export const getTaxPrice = (amount, gstPercentage) => {
  return amount * (gstPercentage / 100);
};

export const blobToBase64 = (blob) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(blob);
    reader.onloadend = () => {
      const base64data = reader.result.split(",")[1]; // Get only the base64 string part
      resolve(base64data);
    };
    reader.onerror = (error) => reject(error);
  });
};

//calculating up/down% of current month wrt last month
export const getUpDownStat = (lastMonth, currentMonth) => {
  if (lastMonth > currentMonth) {
    let v_ = lastMonth - currentMonth;
    v_ = (v_ / lastMonth) * 100;
    return { text: `-${v_.toFixed(2)}%`, color: "error" };
  }

  if (lastMonth < currentMonth) {
    let v_ = currentMonth - lastMonth;
    if (lastMonth === 0) {
      v_ = v_ * 100;
    } else {
      v_ = (v_ / lastMonth) * 100;
    }
    return { text: `+${parseInt(v_)}%`, color: "success" };
  }

  if (lastMonth === currentMonth) {
    return { text: `0%`, color: "success" };
  }
};

//for getting index of value in array
export const floorSearch = (arr, x) => {
  let n = arr.length;

  // If last element is smaller than x
  if (x >= arr[n - 1].productId) return n - 1;

  // If first element is greater than x
  if (x < arr[0].productId) return -1;

  // Linearly search for the first element
  // greater than x
  for (let i = 1; i < n; i++) if (arr[i].productId > x) return i - 1;

  return -1;
};

//for calc selling price with tax
export const calcPriceWT = (priceWOT, tax) => {
  return (
    parseFloat(priceWOT) +
    parseFloat(parseFloat(priceWOT) * (parseFloat(tax) / 100))
  ).toFixed(2);
};

//for converting timestamp
export const timestampConversion = (time) => {
  let createdAt;
  createdAt = time;
  createdAt = createdAt?.nanoseconds / 1000000 + createdAt?.seconds * 1000;
  createdAt = moment.utc(createdAt);
  return createdAt;
};

export const timestampConversionv1 = (time) => {
  let createdAt;
  createdAt = time;
  createdAt = createdAt?.nanoseconds / 1000000 + createdAt?.seconds * 1000;
  return createdAt;
};

export const formatDateField = (date) => {
  let options = { year: "numeric", month: "long", day: "numeric" };
  let today = new Date(date);

  return today.toLocaleDateString("en-US", options);
};

export const formatDateFieldv1 = (date) => {
  let today = moment(new Date(date)).format("MMM Do YY");
  return today;
};

export const formatDateFieldv2 = (date) => {
  let today = moment(new Date(date)).format("Do MMM YY");
  return today;
};

// - 02/02/2023
export const formatDatev3 = (date) => {
  let formattedDate = moment(date).format("DD/MM/YYYY");
  return formattedDate;
};

export const shortFormatDateField = (date) => {
  var options = { year: "numeric", month: "short", day: "numeric" };
  var today = new Date(date);

  return today.toLocaleDateString("en-US", options);
};

export const generatePDF = (doc) => {
  doc.setFont("helvetica", "bold");
  doc.text("DIET PLAN - RAJESH (22/12/2022)", 105, 15, null, null, "center");

  doc.setFont("courier", "normal");
  doc.text("Early Morning", 15, 30);
  doc.setFontSize(11);

  doc.text("Recipe Name - Spinach Corn Sandwich", 20, 40);
  doc.text("Recipe Description - Description about Recipe", 20, 49);
  doc.text("Food type - Party", 20, 58);
  doc.text("Ingredients - Salt, Spinach", 20, 67);
  doc.text("Calories - 3000", 20, 76);
  doc.text("Nutrition Values - Fat:13g, Carbs: 36g", 20, 85);
  doc.text("Category - Fitify", 20, 94);

  var splitText = doc.splitTextToSize(
    "How to prepare - Description about Recipe  123 Description about Recipe Description about Recipe  123 123Description about Recipe  123Description about Recipe  123Description about Recipe  123 \n \nHow to take -  Description about Recipe  123 Description about Recipe Description about Recipe  123 123Description about Recipe  123Description about Recipe  123Description about Recipe  123",
    170
  );
  // var pageHeight = doc.internal.pageSize.height;
  var y = 103;
  for (var i = 0; i < splitText.length; i++) {
    if (y > 375) {
      y = 20;
      doc.addPage();
    }
    doc.text(20, y, splitText[i]);
    y = y + 5;
  }

  doc.save("dietplan-rajesh.pdf");
};

export const referralCodeGenerator = (name) => {
  let res = "";
  for (let i = 0; i < name.length; i++) {
    if (name[i] !== " ") {
      res += name[i];
    }
  }
  return res + new Date().getFullYear();
};

export const loadScript = (src) => {
  return new Promise((resolve) => {
    const script = document.createElement("script");
    script.src = src;
    script.onload = () => {
      resolve(true);
    };

    script.onerror = () => {
      resolve(false);
    };
    document.body.appendChild(script);
  });
};

export function getAge(dateString) {
  var today = new Date();
  var birthDate = new Date(dateString);
  var age = today.getFullYear() - birthDate.getFullYear();
  var m = today.getMonth() - birthDate.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }
  return age;
}

//get the list of names
export function getNames(name) {
  let namesList = [];
  if (name) {
    namesList = [
      name,
      name.toUpperCase(),
      name.toLowerCase(),
      name[0].toUpperCase() + name.slice(1),
      name[0].toUpperCase() + name.slice(1).toLowerCase(),
    ];

    let len = 0;
    let name1_ = "";
    while (len < name.length) {
      if (name[len] === " ") {
        name1_ = name1_.concat(" ");
        if (len + 1 < name.length) {
          name1_ = name1_.concat(name[len + 1].toUpperCase());
          len += 2;
        } else {
          len++;
        }
      } else {
        name1_ = name1_.concat(name[len]);
        len++;
      }
    }

    let name2_ = name[0].toUpperCase() + name.slice(1);
    let name2 = "";
    len = 0;
    while (len < name2_.length) {
      if (name2_[len] === " ") {
        name2 = name2.concat(" ");
        if (len + 1 < name2_.length) {
          name2 = name2.concat(name2_[len + 1].toUpperCase());
          len += 2;
        } else {
          len++;
        }
      } else {
        name2 = name2.concat(name2_[len]);
        len++;
      }
    }
    namesList = [...namesList, name2];
  }

  return namesList;
}

//get last name from full name
export function takeLastName(fullName) {
  let nameArray = fullName.split(" ");
  let lastName = nameArray[nameArray.length - 1];
  return lastName;
}

export function calculatePriceAfterGST(sellingPriceWOT, gst) {
  return roundCurrency(
    parseStrictNumber(sellingPriceWOT) * (parseStrictNumber(gst) / 100)
  );
}

//calculating products price after discount and gst
export function calculateProductFinalPrice(sellingPriceWOT, tax) {
  const priceAfterGst =
    parseStrictNumber(sellingPriceWOT) +
    calculatePriceAfterGST(sellingPriceWOT, tax);
  return roundCurrency(priceAfterGst);
}
export function calculateProductTotalPrice(sellingPriceWOT, tax, quantity) {
  const priceAfterGst =
    parseStrictNumber(sellingPriceWOT) +
    calculatePriceAfterGST(sellingPriceWOT, tax);
  return roundCurrency(priceAfterGst * parseStrictNumber(quantity));
}

export function calculateServiceFinalPrice(sellingPriceWOT, discount, tax) {
  let priceAfterDiscount =
    parseFloat(sellingPriceWOT ?? 0) - parseFloat(discount ?? 0);
  let priceAfterGst = calculatePriceAfterGST(priceAfterDiscount, tax);

  let balancePerProduct = priceAfterDiscount + priceAfterGst;

  return balancePerProduct;
}

//calculating total balance
export function calculateProductTotalBalance(
  priceWOT,
  quantity,
  discount,
  tax,
  amountPaid
) {
  const totalAmount = calculateProductTotalPrice(priceWOT, tax, quantity);
  const balance = roundCurrency(
    totalAmount - parseStrictNumber(amountPaid) - parseStrictNumber(discount)
  );
  return Math.abs(balance) < 0.01 ? 0 : balance;
}

//sorting array of object by name property
export function sortArrayByName(arr) {
  arr.sort(function (a, b) {
    var nameA = a.name.toUpperCase();
    var nameB = b.name.toUpperCase();
    if (nameA < nameB) {
      return -1;
    }
    if (nameA > nameB) {
      return 1;
    }
    return 0;
  });
}

//create invoice id based on timestamp
export function createInvoiceId() {
  let currentDate = new Date();
  let timestamp = currentDate.getTime();
  timestamp = timestamp.toString();
  let invoiceId = "INV-" + timestamp;
  sessionStorage.setItem("invoiceId", invoiceId);
  return invoiceId;
}

//Week1 - Week 100
export function getWeeksArray(allOption) {
  const weeks = [];

  if (allOption) {
    weeks.unshift({ value: "all", label: "All", key: 0 });
  }

  for (let i = 1; i <= 100; i++) {
    weeks.push({
      label: `Week ${i}`,
      value: `Week ${i}`,
      key: i,
    });
  }

  return weeks;
}

export const leadStatusStyle = (key) => {
  switch (key) {
    case "New":
      return "#9DDA11";

    case "In Discussion":
      return "#EA7306";

    case "Converted":
      return "#82D616";

    default:
      return "#DA1D1D";
  }
};

export const calculateBalance = (totalAmount, discount, amountPaid) => {
  if (totalAmount) {
    return (
      parseStrictNumber(totalAmount) -
      parseStrictNumber(discount) -
      parseStrictNumber(amountPaid)
    ).toFixed(2);
  }
};

export function formatNumberInIndianFormat(number) {
  if (number) number = parseFloat(number);
  return number.toLocaleString("en-IN");
}

// Function to extract username from social media URL
export function extractUsername(url) {
  // Remove any leading or trailing slashes
  url = url.replace(/(^\/|\/$)/g, "");

  // Extract the username from the URL
  const parts = url.split("/");
  return parts[parts.length - 1];
}

export const generateFileName = (service, uniquePart) => {
  const currentDate = new Date().toISOString().split("T")[0];
  return uniquePart
    ? `${service}_${uniquePart}_invoice_${currentDate}`
    : `${service}_invoice_${currentDate}`;
};

export function convertTo24HourFormat(time) {
  if (time) {
    const [timeStr, period] = time.split(" ");
    let [hours, minutes] = timeStr.split(":");

    hours = parseInt(hours);
    minutes = parseInt(minutes);

    if (period) {
      if (period.toLowerCase() === "pm" && hours !== 12) {
        hours += 12;
      } else if (period.toLowerCase() === "am" && hours === 12) {
        hours = 0;
      }

      const hoursStr = hours.toString().padStart(2, "0");
      const minutesStr = minutes.toString().padStart(2, "0");
      return `${hoursStr}:${minutesStr}`;
    }
  }

  return time;
}

export const updateRow = (id, updatedData, tableData) => {
  const rowIndex = tableData.findIndex((row) => row.id === id);

  if (rowIndex !== -1) {
    const updatedTableData = [...tableData];

    updatedTableData[rowIndex] = {
      ...updatedTableData[rowIndex],
      ...updatedData,
    };

    return updatedTableData;
  }
};

export const calculateServiceUnpaidBalance = (
  totalAmount,
  discount,
  amountPaid
) => {
  if (totalAmount) {
    return (
      parseStrictNumber(totalAmount) -
      parseStrictNumber(discount) -
      parseStrictNumber(amountPaid)
    ).toFixed(2);
  }
  return 0;
};

export const getServiceGstRate = (item) => {
  if (item?.clientGst !== undefined && item?.clientGst !== null) {
    return parseStrictNumber(item.clientGst);
  }

  if (item?.gstType?.value === "IGST") {
    return parseStrictNumber(item?.igst) || parseStrictNumber(item?.gst);
  }

  const splitGst = parseStrictNumber(item?.sgst) + parseStrictNumber(item?.cgst);
  return splitGst || parseStrictNumber(item?.gst);
};

export const getServiceBaseAmount = (item) => {
  const rowDataBaseAmount = Array.isArray(item?.rowData)
    ? item.rowData.reduce(
        (total, row) => total + parseStrictNumber(row?.amount),
        0
      )
    : 0;

  return (
    parseStrictNumber(item?.totalAmountBeforeTax) ||
    parseStrictNumber(item?.packageAmount) ||
    parseStrictNumber(item?.totalAmount) ||
    rowDataBaseAmount
  );
};

export const getServiceTotalAmountIncludingGst = (item) => {
  const totalAmountWithTax = parseStrictNumber(item?.totalAmountWithTax);
  if (totalAmountWithTax > 0) {
    return roundCurrency(totalAmountWithTax);
  }

  if (Array.isArray(item?.rowData) && item.rowData.length > 0) {
    const rowDataTotal = item.rowData.reduce((total, row) => {
      const amount = parseStrictNumber(row?.amount);
      const gst = parseStrictNumber(row?.gst);
      return total + amount + amount * (gst / 100);
    }, 0);

    if (rowDataTotal > 0) {
      return roundCurrency(rowDataTotal);
    }
  }

  const baseAmount = getServiceBaseAmount(item);
  const gstRate = getServiceGstRate(item);
  return roundCurrency(baseAmount + baseAmount * (gstRate / 100));
};

export const getServiceGstAmount = (item) => {
  const storedTaxAmount = parseStrictNumber(item?.totalTaxAmount);
  if (storedTaxAmount > 0) {
    return roundCurrency(storedTaxAmount);
  }

  const taxAmount =
    getServiceTotalAmountIncludingGst(item) - getServiceBaseAmount(item);
  return roundCurrency(taxAmount);
};

export const getServiceReportBalance = (item) => {
  const totalAmount = getServiceTotalAmountIncludingGst(item);
  const amountPaid = parseStrictNumber(item?.amountPaid);
  const discount = parseStrictNumber(item?.discount);
  const calculatedBalance = roundCurrency(totalAmount - amountPaid - discount);

  if (Math.abs(calculatedBalance) < 0.01) {
    return 0;
  }

  const storedBalance = parseStrictNumber(item?.balance, NaN);
  if (
    calculatedBalance < 0 &&
    Number.isFinite(storedBalance) &&
    storedBalance >= 0
  ) {
    return roundCurrency(storedBalance);
  }

  return calculatedBalance;
};

export const formatAmount = (amount) => {
  const parsedAmount = parseStrictNumber(amount, NaN);
  if (Number.isNaN(parsedAmount)) return "";
  return Number.isInteger(parsedAmount)
    ? String(parsedAmount)
    : parsedAmount.toFixed(2);
};

export function formatDbTimestamp(timestamp) {
  if (timestamp?.seconds) {
    return moment(timestampConversion(timestamp))
      .local()
      .format("MMMM Do YYYY, h:mm a");
  } else {
    return moment(timestamp).local().format("MMMM Do YYYY, h:mm a");
  }
}

export const copyToClipboard = async (
  textToCopy,
  alertMessage,
  errorMessage
) => {
  try {
    if (navigator.clipboard) {
      await navigator.clipboard.writeText(textToCopy);
      toast.success(alertMessage);
    } else {
      toast?.error(errorMessage);
    }
  } catch (error) {
    console.error("Unable to copy to clipboard", error);
  }
};

export const clearInputValueByDocId = (elementId) => {
  document.getElementById(elementId).value = "";
};

export const sortingWithTimestamp = (a, b) => {
  const getTimestamp = (obj) => {
    if (obj.timestamp) {
      return obj.timestamp.seconds * 1000 + obj.timestamp.nanoseconds / 1000000;
    } else if (obj.createdAt) {
      return obj.createdAt.seconds * 1000 + obj.createdAt.nanoseconds / 1000000;
    } else {
      return Infinity; // Return a very large number for objects without timestamps
    }
  };

  const timestampA = getTimestamp(a);
  const timestampB = getTimestamp(b);

  return timestampB - timestampA;
};
export const isAllOptionInserted = (array) =>
  array.some((item) => item.value === "all");

export const calculateGstSummary = (rows, discount, amountPaid) => {
  if (rows) {
    let totalAmountBeforeTax = 0;
    let totalTaxAmount = 0;
    let totalGst = 0;
    const totalAmount = rows.reduce((total, row) => {
      const amount = parseFloat(row.amount) || 0;
      const gst = parseFloat(row.gst) || 0;
      const gstAmount = amount * (gst / 100);

      totalGst += parseFloat(row.gst);
      totalAmountBeforeTax += amount;
      totalTaxAmount += gstAmount;

      return total + amount + gstAmount;
    }, 0);
    // const totalTaxRate = (totalTaxAmount / totalAmountBeforeTax) * 100;
    const totalTaxRate = totalGst;
    const balance =
      parseFloat(totalAmount ?? 0) -
      parseFloat(discount ?? 0) -
      parseFloat(amountPaid ?? 0);
    return {
      balance,
      totalTaxAmount,
      totalTaxRate,
      totalAmountBeforeTax,
      totalAmount,
    };
  }
};

export const isUnregisteredGstTreatment = (gstTreatment) => {
  const treatment = String(
    gstTreatment?.id || gstTreatment?.name || gstTreatment?.value || gstTreatment?.label || ""
  ).toLowerCase();

  return treatment.includes("unregistered");
};

export const calculateGstBalance = (
  totalAmount,
  gstRate,
  discount,
  amountPaid
) => {
  totalAmount = parsePriceValue(totalAmount);
  gstRate = parseStrictNumber(gstRate);
  discount = parseStrictNumber(discount);
  amountPaid = parseStrictNumber(amountPaid);

  const gstAmount = totalAmount * (gstRate / 100);
  const totalAmountWithTax = totalAmount + gstAmount;

  const balance = totalAmountWithTax - discount - amountPaid;

  return {
    balance,
    totalAmountWithTax,
  };
};

export const isObject = (val) =>
  val && typeof val === "object" && !Array.isArray(val);

export const chartTypes = [
  { value: "Weekly", label: "Weekly", key: 1 },
  { value: "Plateau", label: "Plateau", key: 2 },
  { value: "Detox", label: "Detox", key: 3 },
];

export const cleanHTML = (html) => {
  const stripped = html
    // Remove whitespace & invisible tags
    .replace(/<(h\d|p|strong|em|u|b|i|span)[^>]*><br><\/\1>/g, "")
    .replace(/<(h\d|p|strong|em|u|b|i|span)[^>]*><\/\1>/g, "")
    .replace(/<br>/g, "")
    .replace(/&nbsp;/g, "")
    .trim();

  return stripped === "" ? "" : html;
};

export const defaultAllMealSchedules = [
  {
    id: "earlyMorning",
    title: "Early Morning",
    time: "06:30",
    icon: "☕",
  },
  {
    id: "midMorning",
    title: "Mid Morning",
    time: "09:30",
    icon: "🥤",
  },
  {
    id: "breakfast",
    title: "Breakfast",
    time: "08:00",
    icon: "🍳",
  },
  {
    id: "brunch",
    title: "Brunch",
    time: "11:00",
    icon: "🍽️",
  },
  {
    id: "lunch",
    title: "Lunch",
    time: "13:00",
    icon: "🌮",
  },
  {
    id: "midMeal",
    title: "Mid Meal",
    time: "15:30",
    icon: "🥗",
  },
  {
    id: "evening",
    title: "Evening",
    time: "17:00",
    icon: "🍪",
  },
  {
    id: "dinner",
    title: "Dinner",
    time: "20:00",
    icon: "🍲",
  },
  {
    id: "lateNight",
    title: "Late Night",
    time: "22:30",
    icon: "🌙",
  },
];

export const prepareChart = (table, dietMealTimings = []) => {
  const chartObj = {};

  // Add timings from dietMealTimings
  dietMealTimings.forEach((timing) => {
    chartObj[timing.id] = {
      title: timing.title,
      list: [],
    };
  });

  // Add timings from table (even if missing in dietMealTimings)
  table.forEach((entry) => {
    if (Array.isArray(entry.list)) {
      if (!chartObj.hasOwnProperty(entry.timing)) {
        // Fallback for unknown timings
        chartObj[entry.timing] = {
          title: entry.timing
            .replace(/([A-Z])/g, " $1")
            .replace(/^./, (s) => s.toUpperCase()), // convert camelCase to Title Case
          list: [],
        };
      }
      chartObj[entry.timing].list = entry.list;
    }
  });

  // Remove empty meal timings
  Object.keys(chartObj).forEach((key) => {
    if (!chartObj[key].list.length) {
      delete chartObj[key];
    }
  });

  return chartObj;
};

export const getCleanDietPlan = (dietPlan, defaultDietObj) =>
  Object.entries(dietPlan)
    .filter(([key]) => Object.keys(defaultDietObj).includes(key))
    .reduce((acc, [key, val]) => {
      acc[key] = val;
      return acc;
    }, {});

export const extractName = (name) =>
  typeof name === "string" ? name : name?.value || name?.label || "";

export const UnitState = [
  { label: "Litre", value: "Litre", key: 0 },
  { label: "Gms", value: "Gms", key: 1 },
  { label: "Ml", value: "Ml", key: 2 },
  { label: "Table Spoon", value: "Table Spoon", key: 3 },
  { label: "Tablet", value: "Tablet", key: 4 },
  { label: "Softgel", value: "Softgel", key: 4 },
  { label: "Katori", value: "Katori", key: 4 },
  { label: "Tea Cup", value: "Tea Cup", key: 4 },
  { label: "Tea Spoon", value: "Tea Spoon", key: 4 },
  { label: "Kernel", value: "Kernel", key: 4 },
  { label: "Bowl", value: "Bowl", key: 4 },
  { label: "Scoop", value: "Scoop", key: 4 },
  { label: "Piece", value: "Piece", key: 4 },
  { label: "Nuts", value: "Nuts", key: 4 },
  { label: "Servings", value: "Servings", key: 4 },
  { label: "Glass", value: "Glass", key: 4 },
  { label: "Small", value: "Small", key: 4 },
  { label: "Big", value: "Big", key: 4 },
  { label: "Medium", value: "Medium", key: 4 },
];

export const foodCategory = [
  { label: "Vegetables", value: "Vegetables", key: 1 },
  { label: "Fruits", value: "Fruits", key: 2 },
  { label: "Grains", value: "Grains", key: 3 },
  { label: "Protein Foods", value: "Protein Foods", key: 4 },
  { label: "Dairy", value: "Dairy", key: 5 },
  { label: "Oils & Solid Fats", value: "Oils & Solid Fats", key: 6 },
  { label: "Added Sugars", value: "Added Sugars", key: 7 },
  { label: "Beverages", value: "Beverages", key: 8 },
  { label: "Egg", value: "Egg", key: 9 },
  { label: "Non Veg", value: "Non Veg", key: 10 },
  { label: "Sea Food", value: "Sea Food", key: 11 },
  { label: "Dietary Fiber", value: "Dietary Fiber", key: 12 },
  { label: "Vitamins Rich", value: "Vitamins Rich", key: 13 },
  { label: "Minerals Rich", value: "Minerals Rich", key: 14 },
  { label: "Carbs Rich", value: "Carbs Rich", key: 15 },
  { label: "Protein Rich", value: "Protein Rich", key: 16 },
  { label: "Antioxidants Rich", value: "Antioxidants Rich", key: 17 },
  { label: "Anti-Inflammatory,", value: "Anti-Inflammatory,", key: 18 },
  { label: "Fibers Rich", value: "Fibers Rich", key: 19 },
  { label: "Gut Foods", value: "Gut Foods", key: 20 },
  { label: "Others", value: "Others", key: 21 },
];

export const parseNutritionData = (nutrition) => {
  if (!nutrition) return [];

  if (
    Array.isArray(nutrition) &&
    typeof nutrition[0] === "object" &&
    "type" in nutrition[0]
  ) {
    return nutrition.map((item) => ({
      type: item.type?.trim() || "",
      grams: item.grams?.toString() || "",
      unit: item.unit?.trim() || "",
    }));
  }

  if (Array.isArray(nutrition) && typeof nutrition[0] === "string") {
    return nutrition
      .map((str) => {
        const [type, value] = str.split(":");
        if (!type || !value) return null;

        const match = value.trim().match(/^([\d.]+)\s*(\w*)$/);
        return {
          type: type.trim(),
          grams: match ? match[1] : "",
          unit: match ? match[2] : "",
        };
      })
      .filter(Boolean);
  }

  if (typeof nutrition === "object" && !Array.isArray(nutrition)) {
    return Object.entries(nutrition).map(([type, value]) => {
      const match = value.trim().match(/^([\d.]+)\s*(\w*)$/);
      return {
        type: type.trim(),
        grams: match ? match[1] : "",
        unit: match ? match[2] : "",
      };
    });
  }

  return [];
};

export const paymentValue = (paymentMode) =>
  paymentOptions.find((opt) => opt.value === paymentMode) || paymentOptions[0];

export const normalizeOption = (value) =>
  typeof value === "object"
    ? value
    : value
    ? { value, label: value }
    : { value: "", label: "" };

export const matchesSearchTerms = (text, searchInput) => {
  if (!searchInput || typeof text !== "string") return !searchInput?.trim();
  const terms = String(searchInput)
    .trim()
    .toLowerCase()
    .split(/\s+/)
    .filter(Boolean);
  const lowerText = text.toLowerCase();
  return terms.every((term) => lowerText.includes(term));
};

export const mapRecipeToOption = (item, index) => {
  const {
    id = null,
    name = "",
    description = "",
    preparation = "",
    calories = "",
    foodType = [],
    ingredients = [],
    nutritions = [],
    howToTake = "",
    weight = "",
    foodCategory = "",
    qty = "",
    unit = "",
    time = "",
    toggleMode = "AND",
    image = null,
    goesWell = [],
  } = item ?? {};

  return {
    key: index,
    id,
    label: name,
    value: name,
    name: getFormattedClientName(name),
    description,
    preparation,
    calories,
    foodType,
    ingredients,
    nutritions: parseNutritionData(nutritions) ?? [],
    howToTake,
    weight,
    foodCategory: normalizeOption(foodCategory),
    qty,
    unit: normalizeOption(unit),
    time,
    toggleMode,
    image,
    goesWell,
  };
};

export const generateRandomId = () => Math.floor(Math.random() * 1000000);

export function normalizeFoodCategory(category) {
  if (!category) return null;

  if (Array.isArray(category.value) && Array.isArray(category.label)) {
    return category.value.map((val, idx) => ({
      value: val,
      label: category.label[idx] || val,
    }));
  }

  return category;
}

export function getRecentEntryData(array) {
  if (!Array.isArray(array) || array.length === 0) {
    return null;
  }

  return array.reduce((latest, current) => {
    const latestTime =
      (latest?.timestamp?.seconds || 0) * 1e9 +
      (latest?.timestamp?.nanoseconds || 0);
    const currentTime =
      (current?.timestamp?.seconds || 0) * 1e9 +
      (current?.timestamp?.nanoseconds || 0);

    return currentTime > latestTime ? current : latest;
  });
}

const parseTimeToMinutes = (timeStr) => {
  if (!timeStr) return Infinity;
  const match = timeStr.match(/(\d+):(\d+)\s?(AM|PM)/i);
  if (!match) return Infinity;

  let [h, m, ampm] = match.slice(1);
  let hours = parseInt(h, 10) % 12;
  if (ampm.toUpperCase() === "PM") hours += 12;
  return hours * 60 + parseInt(m, 10);
};

export const getEarliestMealTime = (list) => {
  if (!list?.length) return Infinity;
  return Math.min(...list.map((item) => parseTimeToMinutes(item.time)));
};

export const checkActiveService = (clientData, setIsActiveService) => {
  if (!clientData?.services?.length) {
    setIsActiveService(false);
    return;
  }

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const hasActive = clientData.services.some((service) => {
    const start = new Date(service.startDate);
    const end = new Date(service.completionDate);

    start.setHours(0, 0, 0, 0);
    end.setHours(0, 0, 0, 0);

    return today >= start && today <= end;
  });

  setIsActiveService(hasActive);
};

export const chartTypeOptions = [
  {
    value: "all",
    label: "All",
    key: 0,
  },
  { value: "Weekly", label: "Weekly", key: 1 },
  { value: "Plateau", label: "Plateau", key: 2 },
  { value: "Detox", label: "Detox", key: 3 },
];

export function removeTopLevelEmptyKeys(obj) {
  const newObj = {};
  Object.keys(obj).forEach((key) => {
    if (Array.isArray(obj[key]) && obj[key].length === 0) {
      return;
    }
    newObj[key] = obj[key];
  });
  return newObj;
}
